// src/services/reservationService.js
import apiClient from './apiClient';

const reservationService = {
  // ========== SERVICIOS BÁSICOS ==========
  
  // Obtener todas las reservas por cédula
  getAllReservationsByIdCard: async (idCard) => {
    try {
      const response = await apiClient.get('/reservations/getAllReservations', {
        params: { idCard }
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener reservas con idCard ${idCard}:`, error);
      throw error;
    }
  },

  // Crear una nueva reserva
  createReservation: async (reservationData) => {
    try {
      console.log('Enviando datos de reserva:', reservationData);
      const response = await apiClient.post('/reservations/createReservation', reservationData);
      console.log('Respuesta de creación de reserva:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al crear reserva:', {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Obtener una reserva por ID
  getReservationById: async (id) => {
    try {
      const response = await apiClient.get('/reservations/getReservationById', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener reserva con ID ${id}:`, error);
      throw error;
    }
  },

  // Eliminar una reserva por ID
  deleteReservation: async (id) => {
    try {
      const response = await apiClient.delete('/reservations/deleteReservation', {
        params: { id }
      });
      return response.data;
    } catch (error) {
      console.error(`Error al eliminar reserva con ID ${id}:`, error);
      throw error;
    }
  },

  // Obtener la próxima reserva de un usuario
  getNextReservation: async (idCard) => {
    try {
      const response = await apiClient.get('/reservations/getNextReservation', {
        params: { idCard }
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener próxima reserva para idCard ${idCard}:`, error);
      throw error;
    }
  },

  // ========== SERVICIOS AVANZADOS POR PARKING ==========

  // Crear reserva por ID de parking
  createReservationByParkingId: async (parkingId, reservationData) => {
    try {
      console.log(`Creando reserva para parking ${parkingId}:`, reservationData);
      const response = await apiClient.post(`/reservations/createReservationByParkingId/${parkingId}`, reservationData);
      return response.data;
    } catch (error) {
      console.error(`Error al crear reserva para parking ${parkingId}:`, {
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Obtener reservas por ID de parking con filtros y paginación
  getReservationsByParkingId: async (parkingId, options = {}) => {
    try {
      const {
        status,
        startDate,
        endDate,
        page = 1,
        limit = 10
      } = options;

      const params = {
        page,
        limit
      };

      if (status) params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await apiClient.get(`/reservations/getReservationsByParkingId/${parkingId}`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener reservas del parking ${parkingId}:`, error);
      throw error;
    }
  },

  // ========== MÉTODOS AUXILIARES Y UTILITARIOS ==========

  // Verificar disponibilidad de parking en un horario específico
  checkParkingAvailability: async (parkingId, startTime, endTime) => {
    try {
      const reservations = await reservationService.getReservationsByParkingId(parkingId, {
        status: 'confirmed',
        startDate: startTime,
        endDate: endTime
      });
      
      // Si no hay reservas en conflicto, está disponible
      return reservations.data.reservations.length === 0;
    } catch (error) {
      console.error(`Error al verificar disponibilidad del parking ${parkingId}:`, error);
      throw error;
    }
  },

  // Obtener estadísticas de reservas por usuario
  getUserReservationStats: async (idCard) => {
    try {
      const reservations = await reservationService.getAllReservationsByIdCard(idCard);
      
      const stats = {
        total: reservations.length,
        pending: reservations.filter(r => r.status === 'Pendiente').length,
        completed: reservations.filter(r => r.status === 'Realizado').length,
        upcoming: 0,
        today: 0
      };

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      reservations.forEach(reservation => {
        const reservationDate = new Date(reservation.dateIn);
        reservationDate.setHours(0, 0, 0, 0);
        
        if (reservationDate >= today && reservation.status === 'Pendiente') {
          stats.upcoming++;
        }
        
        if (reservationDate.getTime() === today.getTime()) {
          stats.today++;
        }
      });

      return stats;
    } catch (error) {
      console.error(`Error al obtener estadísticas para idCard ${idCard}:`, error);
      throw error;
    }
  },

  // Validar datos de reserva antes de enviar
  validateReservationData: (reservationData) => {
    const requiredFields = ['idCard', 'dateIn', 'timeIn', 'timeOut', 'plate', 'status'];
    const missingFields = requiredFields.filter(field => !reservationData[field]);
    
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes: ${missingFields.join(', ')}`);
    }

    // Validar formato de fecha
    const dateIn = new Date(reservationData.dateIn);
    if (isNaN(dateIn.getTime())) {
      throw new Error('Formato de fecha inválido');
    }

    // Validar que la fecha no sea en el pasado
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dateIn < today) {
      throw new Error('No se pueden hacer reservas para fechas pasadas');
    }

    // Validar formato de hora (HH:mm)
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(reservationData.timeIn) || !timeRegex.test(reservationData.timeOut)) {
      throw new Error('Formato de hora inválido (use HH:mm)');
    }

    // Validar que timeOut sea después de timeIn
    const [inHour, inMin] = reservationData.timeIn.split(':').map(Number);
    const [outHour, outMin] = reservationData.timeOut.split(':').map(Number);
    
    const timeInMinutes = inHour * 60 + inMin;
    const timeOutMinutes = outHour * 60 + outMin;
    
    if (timeOutMinutes <= timeInMinutes) {
      throw new Error('La hora de salida debe ser posterior a la hora de entrada');
    }

    return true;
  },

  // Formatear datos de reserva para mostrar en el frontend
  formatReservationForDisplay: (reservation) => {
    return {
      ...reservation,
      dateInFormatted: new Date(reservation.dateIn).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      }),
      timeInFormatted: reservation.timeIn,
      timeOutFormatted: reservation.timeOut,
      statusFormatted: reservation.status === 'Pendiente' ? 'Pendiente' : 'Realizada',
      duration: reservationService.calculateDuration(reservation.timeIn, reservation.timeOut)
    };
  },

  // Calcular duración entre dos horas
  calculateDuration: (timeIn, timeOut) => {
    const [inHour, inMin] = timeIn.split(':').map(Number);
    const [outHour, outMin] = timeOut.split(':').map(Number);
    
    const timeInMinutes = inHour * 60 + inMin;
    const timeOutMinutes = outHour * 60 + outMin;
    
    const durationMinutes = timeOutMinutes - timeInMinutes;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  },

  // Obtener reservas del día actual
  getTodayReservations: async (idCard) => {
    try {
      const allReservations = await reservationService.getAllReservationsByIdCard(idCard);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      return allReservations.filter(reservation => {
        const reservationDate = new Date(reservation.dateIn);
        reservationDate.setHours(0, 0, 0, 0);
        return reservationDate.getTime() === today.getTime();
      });
    } catch (error) {
      console.error(`Error al obtener reservas de hoy para idCard ${idCard}:`, error);
      throw error;
    }
  },

  // Cancelar reserva (cambiar estado a cancelado si se implementa)
  cancelReservation: async (id, reason = '') => {
    try {
      // Por ahora elimina la reserva, pero podrías cambiar el estado a 'Cancelado'
      const response = await reservationService.deleteReservation(id);
      console.log(`Reserva ${id} cancelada. Razón: ${reason}`);
      return response;
    } catch (error) {
      console.error(`Error al cancelar reserva ${id}:`, error);
      throw error;
    }
  },

    getAllReservationsAd: async () => {
        try {
          
            const response = await apiClient.get('/reservations/getAllReservationsAd');
            return response.data;

        } catch (error) {
            console.error('Error al obtener las reservas:', error.response?.data || error.message);

            // Manejo específico de errores
            if (error.response?.status === 403) {
                const errorMsg = error.response.data?.error || 'Acceso denegado';
                console.error('Error 403 detallado:', error.response.data);

                // Verificar si es problema de rol o de registro de admin
                if (errorMsg.includes('permisos de administrador')) {
                    alert('Tu usuario no tiene permisos de administrador. Contacta al SuperAdmin para obtener acceso.');
                } else if (errorMsg.includes('administrador no encontrado')) {
                    alert('Tu usuario no está registrado como administrador en el sistema. Contacta al SuperAdmin.');
                } else {
                    alert(`Error de permisos: ${errorMsg}`);
                }
            } else if (error.response?.status === 401) {
                alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
            } else {
                alert('Error de conexión. Verifica que el servidor esté funcionando.');
            }

            throw error;
        }
    },




};

export default reservationService;